import type { PayloadAction } from '@reduxjs/toolkit';
import config from 'Cloud/config';
import { logger } from 'lib/logger';
import { parse, stringify } from 'qs';
import { GP_NESTED_FOLDERS_ENABLED } from 'reactApp/appHelpers/featuresHelpers/features/gpNestedFolders';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { getClosestDomainFolder } from 'reactApp/modules/domainFolders/domainFolders.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getFolderPath, getSlicedHome, isFolder, removeTailSlash } from 'reactApp/modules/file/utils';
import { getHomeItemById } from 'reactApp/modules/home/home.selectors';
import { historyPush, routerDomainFolder, routerStart } from 'reactApp/modules/router/router.module';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import type { IHistoryPush } from 'reactApp/modules/router/router.types';
import { getSortById } from 'reactApp/modules/sort/sort.selectors';
import { getPrivateList } from 'reactApp/modules/start/start.saga';
import { getLoadMoreLimit } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType, INTEGRATION_STORAGES } from 'reactApp/modules/storage/storage.types';
import { checkViewerAtIdChange } from 'reactApp/modules/viewer/viewer.module';
import { WorkspaceSuperAppService } from 'reactApp/services';
import { getEncodePath, getPathName, getStorageAwareFolderPath } from 'reactApp/utils/urlHelper';
import { call, put, select } from 'redux-saga/effects';

const SERVER_HOME = config.get('serverSideFolders')?.folder?.home || config.get('serverSideFolders')?.folder?.path;

const ensureSuperappSearch = (original?: string): string | undefined => {
    if (!WorkspaceSuperAppService.isEnabled) {
        return original;
    }

    const baseSearch = original ?? (typeof window !== 'undefined' ? window.location.search : '');

    const params = parse(baseSearch, { ignoreQueryPrefix: true });

    if ('superapp' in params) {
        return baseSearch;
    }

    return stringify({ ...params, superapp: 1 }, { addQueryPrefix: true });
};

export function* changeHistorySaga(action: PayloadAction<IHistoryPush>) {
    const { id, search, isFolderRename } = action.payload;
    const finalSearch = ensureSuperappSearch(search);
    const isPhone = yield select(EnvironmentSelectors.isPhone);

    let idFixed;
    try {
        idFixed = decodeURIComponent(removeTailSlash(id));
    } catch (error) {
        idFixed = id;
        logger.error(error);
    }
    const routeId = yield select(getCurrentRouteId);

    if (routeId !== ROOT_FOLDER_ID && routeId !== idFixed && routeId.toLowerCase() === idFixed.toLowerCase()) {
        idFixed = routeId;
    }

    let item = yield select(getHomeItemById, idFixed);
    if (!item && idFixed !== ROOT_FOLDER_ID) {
        try {
            const sort = yield select((state) => getSortById(state, idFixed));
            item = yield call(getPrivateList, idFixed, getLoadMoreLimit(EStorageType.home), 0, sort);
        } catch (error) {
            logger.error(error);
        }
    }

    const currentIntegrationStorage = INTEGRATION_STORAGES[yield select(getCurrentStorage)];
    const storage = currentIntegrationStorage || EStorageType.home;
    const isDirectPath = !item; // при первом заходе на страницу
    let isHomeFile = false;
    let folderPath;

    if (isDirectPath) {
        const slicedPath = decodeURIComponent(removeTailSlash(getSlicedHome(getPathName())));
        const isFolderItem = SERVER_HOME === slicedPath;
        folderPath = decodeURIComponent(getFolderPath(id, isFolderItem, false));

        isHomeFile = folderPath === ROOT_FOLDER_ID && !isFolderItem;

        if (id === '/') {
            yield put(historyPush({ id: '/', search: finalSearch })); // если переходим на корень хомяка и меню схлопнуто
        }
    } else {
        folderPath = getFolderPath(item.home, isFolder(item), false);
        const pathname = getStorageAwareFolderPath(storage, getEncodePath(item.home));

        yield put(historyPush({ id: pathname, search: finalSearch }));
    }

    if (GP_NESTED_FOLDERS_ENABLED && (yield select(getClosestDomainFolder, folderPath))) {
        yield put(routerDomainFolder({ id: folderPath }));
    }

    if (isFolderRename) {
        yield put(historyPush({ id: `/${storage}${idFixed}` }));
    }

    if (item && !isPhone) {
        // TODO: routing: перенести все в общую ротуинг сагу CLOUDWEB-13172
        yield put(checkViewerAtIdChange({ id: item.id, storage: EStorageType.home }));
    }

    // при переходе стрелками браузера в id содержится home и становится сложно отличить от первого захода на страницу
    if (folderPath === ROOT_FOLDER_ID && isDirectPath && !isHomeFile) {
        folderPath = id || ROOT_FOLDER_ID;
    }

    yield put(
        routerStart({
            id: folderPath,
            params: {},
            storage,
            url: folderPath,
            __isFolder: true,
            __parent: '/',
        })
    );
}
